import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  History,
  Search,
  RefreshCw,
  Download,
  Megaphone,
  Bot,
  User,
  ExternalLink
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import messageService, { SentMessage } from '../../services/whatsapp/messageService';

const formatDate = (timestamp: number): string =>
  new Date(timestamp).toLocaleString([], {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  });

/** Tones mirror the bubble status icons so the two views agree at a glance. */
const STATUS_TONE: Record<string, string> = {
  queued: 'bg-gray-100 text-gray-600',
  sent: 'bg-gray-100 text-gray-700',
  delivered: 'bg-primary-50 text-primary-700',
  read: 'bg-success-50 text-success-600',
  failed: 'bg-danger-50 text-danger-600'
};

const STATUS_FILTERS = [
  { label: 'All', value: '' },
  { label: 'Sent', value: 'sent' },
  { label: 'Delivered', value: 'delivered' },
  { label: 'Read', value: 'read' },
  { label: 'Failed', value: 'failed' }
];

const TYPE_FILTERS = [
  { label: 'All types', value: '' },
  { label: 'Template', value: 'template' },
  { label: 'Text', value: 'text' },
  { label: 'Image', value: 'image' },
  { label: 'Document', value: 'document' }
];

const PAGE_SIZE = 50;

/** Who sent it — campaign, bot, or a person. */
const Source: React.FC<{ message: SentMessage }> = ({ message }) => {
  if (message.broadcastId) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-gray-600">
        <Megaphone className="h-3 w-3 shrink-0 text-primary-600" />
        {message.broadcastName || 'Campaign'}
      </span>
    );
  }

  if (message.automationRunId) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-gray-600">
        <Bot className="h-3 w-3 shrink-0 text-warning-600" />
        Bot
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 text-xs text-gray-600">
      <User className="h-3 w-3 shrink-0 text-gray-400" />
      {message.sentByAgentName || 'Agent'}
    </span>
  );
};

/**
 * Sent messages — every outbound message across all conversations.
 *
 * The inbox answers "what did we say to this contact". This answers "what did
 * we send, to anyone", which is the view you need to audit a campaign or chase
 * a delivery failure without opening threads one at a time.
 *
 * Paginated by cursor rather than page number: the underlying query is ordered
 * by createdAt, and deep offsets bill for every skipped document.
 */
const WhatsAppSentMessages: React.FC = () => {
  const navigate = useNavigate();

  const [messages, setMessages] = useState<SentMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cursor, setCursor] = useState<number | null>(null);

  const [status, setStatus] = useState('');
  const [type, setType] = useState('');
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');

  // Debounce the search so typing does not fire a query per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setAppliedSearch(search.trim()), 350);
    return () => clearTimeout(timer);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await messageService.listSent({
        status: status || undefined,
        type: type || undefined,
        search: appliedSearch || undefined,
        limit: PAGE_SIZE
      });
      setMessages(result.messages);
      setCursor(result.nextCursor);
    } catch (err: any) {
      setError(err.message || 'Could not load sent messages');
    } finally {
      setLoading(false);
    }
  }, [status, type, appliedSearch]);

  useEffect(() => {
    load();
  }, [load]);

  const loadMore = async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);

    try {
      const result = await messageService.listSent({
        status: status || undefined,
        type: type || undefined,
        search: appliedSearch || undefined,
        before: cursor,
        limit: PAGE_SIZE
      });
      setMessages((previous) => [...previous, ...result.messages]);
      setCursor(result.nextCursor);
    } catch (err: any) {
      setError(err.message || 'Could not load more');
    } finally {
      setLoadingMore(false);
    }
  };

  const counts = useMemo(() => {
    const tally = { total: messages.length, delivered: 0, read: 0, failed: 0 };
    messages.forEach((message) => {
      if (message.status === 'delivered') tally.delivered += 1;
      if (message.status === 'read') tally.read += 1;
      if (message.status === 'failed') tally.failed += 1;
    });
    return tally;
  }, [messages]);

  /** Exports what is currently loaded, which is what the operator can see. */
  const exportCsv = () => {
    const header = [
      'Sent at',
      'Contact',
      'Phone',
      'Type',
      'Template',
      'Message',
      'Status',
      'Source',
      'Error'
    ];

    const rows = messages.map((message) => [
      new Date(message.createdAt).toISOString(),
      message.contactName,
      message.contactPhone,
      message.type,
      message.templateName || '',
      message.text,
      message.status,
      message.broadcastName || (message.automationRunId ? 'Bot' : message.sentByAgentName || 'Agent'),
      message.error?.title || ''
    ]);

    const csv = [header, ...rows]
      .map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')
      )
      .join('\n');

    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `sent-messages-${Date.now()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="wa-theme flex h-full flex-col bg-white">
      <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 px-4 py-3">
        <h1 className="mr-auto flex items-center gap-2 text-sm font-semibold text-gray-900">
          <History className="h-4 w-4 text-primary-600" />
          Sent messages
        </h1>

        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search message, template or campaign"
            className="w-64 rounded-lg border-gray-300 py-1.5 pl-7 pr-2 text-xs text-gray-900 placeholder:text-gray-400 focus:border-primary-500 focus:ring-primary-500"
          />
        </div>

        <select
          value={type}
          onChange={(event) => setType(event.target.value)}
          className="rounded-lg border-gray-300 py-1.5 text-xs text-gray-900 focus:border-primary-500 focus:ring-primary-500"
        >
          {TYPE_FILTERS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>

        <button
          type="button"
          onClick={exportCsv}
          disabled={messages.length === 0}
          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <Download className="h-3.5 w-3.5" />
          Export
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-1.5 border-b border-gray-200 bg-gray-50 px-4 py-2">
        {STATUS_FILTERS.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => setStatus(option.value)}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              status === option.value
                ? 'bg-primary-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            {option.label}
          </button>
        ))}

        <span className="ml-auto text-[11px] text-gray-500">
          {counts.total} loaded · {counts.delivered} delivered · {counts.read} read
          {counts.failed > 0 && (
            <span className="text-danger-600"> · {counts.failed} failed</span>
          )}
        </span>
      </div>

      {error && (
        <div className="bg-danger-50 px-4 py-2 text-sm text-danger-600">
          {error}
          <button
            type="button"
            onClick={() => setError(null)}
            className="ml-2 font-medium underline"
          >
            Dismiss
          </button>
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-auto">
        <table className="w-full border-collapse text-sm">
          <thead className="sticky top-0 z-10 bg-gray-50">
            <tr className="text-left text-xs font-medium text-gray-600">
              <th className="px-3 py-2.5">Sent</th>
              <th className="px-3 py-2.5">Contact</th>
              <th className="px-3 py-2.5">Message</th>
              <th className="px-3 py-2.5">Type</th>
              <th className="px-3 py-2.5">Source</th>
              <th className="px-3 py-2.5">Status</th>
              <th className="px-3 py-2.5 text-right">Thread</th>
            </tr>
          </thead>

          <tbody>
            {loading && (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-xs text-gray-500">
                  Loading…
                </td>
              </tr>
            )}

            {!loading && messages.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-xs text-gray-500">
                  No sent messages match these filters.
                </td>
              </tr>
            )}

            {!loading &&
              messages.map((message) => (
                <tr
                  key={message.id}
                  className="border-t border-gray-100 align-top hover:bg-gray-50"
                >
                  <td className="whitespace-nowrap px-3 py-2.5 text-xs text-gray-500">
                    {formatDate(message.createdAt)}
                  </td>

                  <td className="px-3 py-2.5">
                    <span className="block text-xs font-medium text-gray-900">
                      {message.contactName || 'Unknown'}
                    </span>
                    <span className="block text-[11px] text-gray-500">
                      {message.contactPhone}
                    </span>
                  </td>

                  <td className="max-w-md px-3 py-2.5">
                    <span className="line-clamp-2 text-xs text-gray-700">
                      {message.text || <em className="text-gray-400">No text</em>}
                    </span>
                    {message.templateName && (
                      <span className="mt-0.5 inline-block rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600">
                        {message.templateName}
                      </span>
                    )}
                    {message.error && (
                      <span className="mt-0.5 block text-[11px] text-danger-600">
                        {message.error.title}
                      </span>
                    )}
                  </td>

                  <td className="px-3 py-2.5 text-xs capitalize text-gray-600">
                    {message.type}
                  </td>

                  <td className="px-3 py-2.5">
                    <Source message={message} />
                  </td>

                  <td className="px-3 py-2.5">
                    <span
                      className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-medium capitalize ${
                        STATUS_TONE[message.status] || 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {message.status}
                    </span>
                  </td>

                  <td className="px-3 py-2.5 text-right">
                    {message.conversationId && (
                      <button
                        type="button"
                        onClick={() =>
                          navigate(`/whatsapp?conversation=${message.conversationId}`)
                        }
                        className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700"
                      >
                        Open
                        <ExternalLink className="h-3 w-3" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>

        {cursor && !loading && (
          <div className="flex justify-center p-4">
            <button
              type="button"
              onClick={loadMore}
              disabled={loadingMore}
              className="rounded-lg border border-gray-300 px-4 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {loadingMore ? 'Loading…' : 'Load more'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsAppSentMessages;
